import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { referralPartnerApi } from '@/api/referralPartner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function PartnerResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset link. Please request a new password reset.');
    }
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!token) {
      setError('Invalid or missing reset link.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await referralPartnerApi.resetPassword(token, newPassword);
      setMessage('Password updated. Redirecting to sign in…');
      setTimeout(() => navigate('/partner/login'), 2000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0f1a] px-4 py-10">
      <div
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(34,211,238,0.25), transparent)',
        }}
      />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/80 p-5 sm:p-8 shadow-2xl backdrop-blur-xl">
        {children}
      </div>
    </div>
  );

  if (!token) {
    return shell(
      <>
        <h1 className="text-center text-xl font-bold text-white">Invalid reset link</h1>
        <p className="mt-2 text-center text-sm text-slate-400">
          Use the link from your email or request a new reset.
        </p>
        <Button
          type="button"
          className="mt-6 w-full h-11 bg-gradient-to-r from-cyan-500 to-violet-600"
          onClick={() => navigate('/partner/forgot-password')}
        >
          Request new link
        </Button>
        <p className="mt-4 text-center text-sm text-slate-400">
          <Link to="/partner/login" className="text-cyan-400 hover:underline">
            Back to sign in
          </Link>
        </p>
      </>,
    );
  }

  return shell(
    <>
      <p className="text-center text-xs font-semibold uppercase tracking-widest text-cyan-400">
        Partner Portal
      </p>
      <h1 className="mt-2 text-center text-2xl font-bold text-white">Set new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 h-11 pr-10"
              minLength={6}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <div>
          <div className="relative">
            <Input
              type={showConfirm ? 'text' : 'password'}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 h-11 pr-10"
              minLength={6}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        {message ? <p className="text-sm text-emerald-400">{message}</p> : null}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500"
        >
          {loading ? 'Saving…' : 'Reset password'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-400">
        <Link to="/partner/login" className="font-medium text-cyan-400 hover:underline">
          Back to sign in
        </Link>
      </p>
    </>,
  );
}
